import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import * as React from 'react';
import useWindowSize from '../../hooks/useWindowSize.js';
import { t } from '../../localization.js';
import Beacon from '../Beacon/Beacon.js';
import Button from '../Button/Button.js';
import Divider from '../Divider/Divider.js';
import styles from './Popover.module.scss.js';

var POPOVER_THEMES;
(function (POPOVER_THEMES) {
    POPOVER_THEMES["DEFAULT"] = "default";
    POPOVER_THEMES["CLIENT"] = "client";
})(POPOVER_THEMES || (POPOVER_THEMES = {}));
var POPOVER_POSITIONS;
(function (POPOVER_POSITIONS) {
    POPOVER_POSITIONS["TOP"] = "top";
    POPOVER_POSITIONS["RIGHT"] = "right";
    POPOVER_POSITIONS["BOTTOM"] = "bottom";
    POPOVER_POSITIONS["LEFT"] = "left";
})(POPOVER_POSITIONS || (POPOVER_POSITIONS = {}));
var POPOVER_ALIGNS;
(function (POPOVER_ALIGNS) {
    POPOVER_ALIGNS["CENTER"] = "center";
    POPOVER_ALIGNS["TOP"] = "top";
    POPOVER_ALIGNS["RIGHT"] = "right";
    POPOVER_ALIGNS["BOTTOM"] = "bottom";
    POPOVER_ALIGNS["LEFT"] = "left";
})(POPOVER_ALIGNS || (POPOVER_ALIGNS = {}));
function getBreakpoint(breakpoints) {
    if (typeof breakpoints === 'string') {
        return breakpoints;
    }
    var firstKey = Object.keys(breakpoints)[0];
    return breakpoints[Number(firstKey)];
}
function getSortedBreakpointsKeys(breakpoints) {
    if (!breakpoints || typeof breakpoints === 'string') {
        return null;
    }
    return Object.keys(breakpoints).map(Number).sort(function (a, b) { return b - a; });
}
var Popover = React.forwardRef(function Popover(_a, popoverRef) {
    var _b, _c;
    var align = _a.align, blockClassName = _a.blockClassName, centerBeacon = _a.centerBeacon, className = _a.className, content = _a.content, header = _a.header, isVisible = _a.isVisible, position = _a.position, _d = _a.theme, theme = _d === void 0 ? POPOVER_THEMES.DEFAULT : _d, _e = _a.windowResizeDebounce, windowResizeDebounce = _e === void 0 ? 200 : _e, withBeacon = _a.withBeacon, children = _a.children, _f = _a.isActionBtnVisible, isActionBtnVisible = _f === void 0 ? false : _f, _g = _a.actionBtnText, actionBtnText = _g === void 0 ? t('Next') : _g, onClose = _a.onClose, onClick = _a.onClick, onActionBtnClick = _a.onActionBtnClick;
    var uid = "".concat(React.useId(), "_").concat(Math.floor(Math.random() * 1e3));
    var _h = React.useState(getBreakpoint(position)), guidePosition = _h[0], setGuidePosition = _h[1];
    var _j = React.useState(align && getBreakpoint(align)), guideAlign = _j[0], setGuideAlign = _j[1];
    var windowWidth = useWindowSize(windowResizeDebounce).windowWidth;
    var sortedPositionBreakpoints = getSortedBreakpointsKeys(position);
    var sortedAlignBreakpoints = getSortedBreakpointsKeys(align);
    React.useEffect(function () {
        var updatePosition = function () {
            if (!sortedPositionBreakpoints) {
                return;
            }
            for (var _i = 0, sortedPositionBreakpoints_1 = sortedPositionBreakpoints; _i < sortedPositionBreakpoints_1.length; _i++) {
                var breakpoint = sortedPositionBreakpoints_1[_i];
                if (windowWidth >= breakpoint) {
                    setGuidePosition(position[breakpoint]);
                    break;
                }
            }
        };
        var updateAlign = function () {
            if (!align || !sortedAlignBreakpoints) {
                return;
            }
            for (var _i = 0, sortedAlignBreakpoints_1 = sortedAlignBreakpoints; _i < sortedAlignBreakpoints_1.length; _i++) {
                var breakpoint = sortedAlignBreakpoints_1[_i];
                if (windowWidth >= breakpoint) {
                    setGuideAlign(align[breakpoint]);
                    break;
                }
            }
        };
        sortedPositionBreakpoints && updatePosition();
        sortedAlignBreakpoints && updateAlign();
    }, [windowWidth, sortedPositionBreakpoints, sortedAlignBreakpoints]);
    var getBackground = function () { return (jsx("div", { className: styles.popoverBg, children: jsxs("svg", { className: styles.svg, width: "100%", height: "100%", xmlns: 'http://www.w3.org/2000/svg', children: [jsxs("defs", { children: [jsxs("clipPath", { id: "wru-popover-clip-".concat(uid), children: [jsx("rect", { className: classNames(styles.svgTail, styles.svgMasked) }), jsx("rect", { className: classNames(styles.svgBody, styles.svgMasked) })] }), jsxs("filter", { id: "wru-popover-filter", children: [jsx("feFlood", { floodColor: "var(--wru-popover-stroke-color)", floodOpacity: 1, result: "STROKE" }), jsx("feComposite", { operator: "xor", result: "STROKED", in: "STROKE", in2: "SourceAlpha" }), jsx("feMerge", { children: jsx("feMergeNode", { in: "STROKED" }) })] })] }), jsxs("g", { filter: "url(#wru-popover-filter)", clipPath: "url(#wru-popover-clip-".concat(uid, ")"), children: [jsx("rect", { className: styles.svgTail }), jsx("rect", { className: styles.svgBody })] })] }, "svg-bg-".concat(uid)) })); };
    var wrapperClass = classNames(styles.wrapper, className, (_b = {},
        _b[styles["theme-".concat(theme)]] = theme !== POPOVER_THEMES.DEFAULT,
        _b[styles["position-".concat(guidePosition)]] = guidePosition,
        _b[styles["align-".concat(guideAlign)]] = !!guideAlign && guideAlign !== POPOVER_ALIGNS.CENTER,
        _b[styles['withActionBtn']] = isActionBtnVisible,
        _b));
    return (jsxs("div", { className: wrapperClass, onClick: onClick, children: [children, isVisible && (jsxs(Fragment, { children: [withBeacon && jsx(Beacon, { className: classNames(styles.beacon, (_c = {},
                            _c[styles.centerBeacon] = centerBeacon,
                            _c)) }), jsx("div", { className: styles.popover, ref: popoverRef, children: jsxs("div", { className: classNames(styles.clipped, blockClassName), style: { clipPath: "url(#wru-popover-clip-".concat(uid, ")") }, children: [getBackground(), jsxs("div", { className: styles.popoverBody, children: [header && (jsx("div", { className: styles.header, children: jsxs("div", { className: styles.headerInner, children: [header, jsx("div", { className: styles.close, onClick: onClose })] }) })), content && (jsxs(Fragment, { children: [jsx(Divider, { className: styles.divider, type: "major_light" }), jsx("div", { className: styles.content, children: jsx("div", { className: styles.contentInner, children: content }) })] })), isActionBtnVisible && (jsxs(Fragment, { children: [jsx(Divider, { className: styles.divider, type: "major_light" }), jsx("div", { className: styles.actions, children: jsx(Button, { isFlat: true, isSmaller: true, onClick: onActionBtnClick, children: actionBtnText }) })] }))] })] }) })] }))] }));
});
var Popover$1 = React.memo(Popover);

export { POPOVER_ALIGNS, POPOVER_POSITIONS, POPOVER_THEMES, Popover$1 as default };
//# sourceMappingURL=Popover.js.map
